/*
 * Feed Health Table - displays feed health metrics
 */

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRightIcon } from "@heroicons/react/24/outline";

import { useFeedHealth } from "@api/queries";
import { EmptyListState } from "@components/emptystates";
import { classNames, formatRelativeTime } from "@utils";
import type { FeedHealth } from "@/types";
import {
  filterFeedHealth,
  getFeedHealthSummary,
  type FeedHealthFilter,
} from "./operations";

function getFeedStatus(feed: FeedHealth) {
  if (feed.enabled && feed.error_count > 0) {
    return { label: "Failing", dot: "bg-red-500" };
  }
  if (feed.enabled && feed.articles_last_24h === 0) {
    return { label: "Quiet", dot: "bg-yellow-500" };
  }
  if (feed.enabled) {
    return { label: "Healthy", dot: "bg-green-500" };
  }
  if (feed.error_count > 0) {
    return { label: "Disabled · retained errors", dot: "bg-gray-400" };
  }
  return { label: "Disabled", dot: "bg-gray-400" };
}

function FeedHealthRow({ feed }: { feed: FeedHealth }) {
  const isFailing = feed.enabled && feed.error_count > 0;
  const isQuiet = feed.enabled && feed.error_count === 0 && feed.articles_last_24h === 0;
  const status = getFeedStatus(feed);

  return (
    <tr
      className={classNames(
        "hover:bg-gray-50 dark:hover:bg-gray-750",
        isFailing && "bg-red-50 dark:bg-red-900/10",
        isQuiet && "bg-yellow-50 dark:bg-yellow-900/10",
      )}
    >
      <td className="whitespace-nowrap py-3 pl-5 pr-3">
        <Link
          to="/settings/feeds"
          className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          {feed.name}
        </Link>
      </td>
      <td className="whitespace-nowrap px-3 py-3">
        <span className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <span className={classNames("h-2 w-2 rounded-full", status.dot)} aria-hidden="true" />
          {status.label}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-center text-sm text-gray-900 dark:text-gray-100">
        {feed.articles_last_24h}
      </td>
      <td
        className={classNames(
          "whitespace-nowrap px-3 py-3 text-center text-sm font-medium",
          isFailing
            ? "text-red-600 dark:text-red-400"
            : feed.error_count > 0
              ? "text-gray-500 dark:text-gray-400"
              : "text-green-600 dark:text-green-400",
        )}
      >
        {feed.error_count}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
        {feed.last_run_at ? formatRelativeTime(feed.last_run_at) : "-"}
      </td>
      <td className="py-3 pl-3 pr-5">
        {feed.last_error ? (
          <span
            className="block max-w-xs truncate text-xs text-red-600 dark:text-red-400"
            title={feed.last_error}
          >
            {feed.last_error}
          </span>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
        )}
      </td>
    </tr>
  );
}

export const FeedHealthTable = ({ variant }: { variant: "dashboard" | "settings" }) => {
  const [manualExpanded, setManualExpanded] = useState<boolean | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FeedHealthFilter>("all");
  const { isLoading, isError, error, data } = useFeedHealth();

  if (isLoading) {
    return (
      <div className="mt-4 flex flex-col">
        <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-200">Feed Health</h3>
        <div className="mt-4 animate-pulse"><EmptyListState text="Loading..." /></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mt-4 flex flex-col">
        <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-200">Feed Health</h3>
        <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">
          Failed to load feed health: {error.message}
        </p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="mt-4 flex flex-col">
        <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-200">Feed Health</h3>
        <div className="mt-4"><EmptyListState text="No feeds configured yet." /></div>
      </div>
    );
  }

  const summary = getFeedHealthSummary(data);
  const isExpanded = manualExpanded ?? (variant === "settings" || summary.failing > 0);
  const visibleFeeds = filterFeedHealth(data, variant === "dashboard" ? "dashboard" : filter, search);

  return (
    <div className="mt-4 flex flex-col">
      <button
        type="button"
        className="flex items-center gap-2 text-left text-lg font-semibold leading-6 text-black transition-colors hover:text-gray-700 dark:text-white dark:hover:text-gray-300"
        onClick={() => setManualExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="feed-health-table"
      >
        <ChevronRightIcon
          className={classNames("h-5 w-5 transition-transform", isExpanded && "rotate-90")}
          aria-hidden="true"
        />
        <span>Feed Health</span>
        <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
          {summary.enabled} enabled · {summary.failing} failing · {summary.quiet} quiet
        </span>
      </button>

      {isExpanded && (
        <div id="feed-health-table" className="mb-2 mt-4 w-full">
          {variant === "settings" && (
            <div className="mb-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search feed health"
                aria-label="Search feed health"
                className="block w-full rounded-md border-gray-300 bg-white text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:max-w-xs"
              />
              <select
                value={filter}
                onChange={(event) => setFilter(event.target.value as FeedHealthFilter)}
                aria-label="Filter feed health"
                className="rounded-md border-gray-300 bg-white text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              >
                <option value="all">All feeds</option>
                <option value="healthy">Healthy</option>
                <option value="failing">Failing</option>
                <option value="quiet">Quiet</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          )}

          {visibleFeeds.length === 0 ? (
            <EmptyListState text={variant === "dashboard" ? "No enabled feeds need attention." : "No feeds match these filters."} />
          ) : (
            <div className="overflow-auto rounded-md border border-gray-250 bg-white shadow-table dark:border-gray-775 dark:bg-gray-800">
              <table aria-label="Feed health" className="w-full divide-y divide-gray-200 rounded-md dark:divide-gray-750">
                <thead className="bg-gray-100 dark:bg-gray-850">
                  <tr>
                    <th scope="col" className="rounded-tl-md py-3 pl-5 pr-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">Name</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">Status</th>
                    <th scope="col" className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">Articles/24h</th>
                    <th scope="col" className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">Errors</th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">Last run</th>
                    <th scope="col" className="rounded-tr-md py-3 pl-3 pr-5 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">Last error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-150 dark:divide-gray-750">
                  {visibleFeeds.map((feed) => <FeedHealthRow key={feed.feed_id} feed={feed} />)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
